import { BindAll } from 'lodash-decorators'
import type { CityTypeInfo, IRecommendCitiesResp } from '@xt/client/entities/kindred-spirit/kindred-spirit'
import { $service } from '@xt/client/experimental/request/service'
import { EnumApiTonghao } from '@xt/client/api/kindred-spirit'
import { action, observable, computed, toJS } from 'mobx'
import { reportEntryLog } from '@xt/client/utils/log'
import { getStaticJson } from '@xt/client/utils/get-static-json'
import { PagePagination } from '@xt/client/utils/infinite-loading/index'
import { getCityCascader, type RawCityItem } from '@xt/client/dataSource/china/index'

type RecommendCityParamType = {
  adcode: string
  recommend: boolean
  page: number
  pageSize: number
  statusArr: string
}

export enum EnumCityStatus {
  /** 未创建 */
  NotInited = 0,
  /** 助力中 */
  Assistance = 1,
  /** 已创建 */
  Inited = 2
}

@BindAll()
export class Cities {
  constructor() {}

  @observable cities: Array<CityTypeInfo> = []
  @observable locateCity: CityTypeInfo | null = null
  @observable joinedCity: CityTypeInfo | null = null

  pagePagination = new PagePagination({
    url: EnumApiTonghao.GuildList,
    needCache: false,
    needLastId: false,
    pageSize: 20,
    mapValues: res => {
      return {
        dataSource: this.transformCityInfo(res.data)
      }
    }
  })

  @computed
  get firstCity() {
    return this.recommendCities[0]
  }

  @computed
  get recommendCities() {
    const rawCities = toJS(this.cities)

    if (!this.locateCity?.id) {
      return toJS(rawCities).slice(1, 8)
    }

    const targetCity = toJS(this.locateCity)
    const locateId = targetCity.id
    const existIndex = rawCities.findIndex(item => item.id === locateId)

    if (existIndex > -1) {
      rawCities.splice(existIndex, 1)
    }
    rawCities.splice(1, 0, targetCity)

    return rawCities.slice(1, 8)
  }

  transformCityInfo(city: Array<IRecommendCitiesResp>): Array<CityTypeInfo> {
    return city.map(i => {
      let subTitle: string = ''

      if (i.memberCount || i.status === EnumCityStatus.Inited) {
        subTitle = `${i.memberCount}位桐学加入`
      } else {
        subTitle = i.status === EnumCityStatus.Assistance ? '即将开放' : i.recruitStatus === true ? '准备中...' : ''
      }

      return {
        id: i.id,
        name: i.guildName,
        count: i.memberCount,
        cover: i.pcCoverPhoto,
        adcode: i.adcode,
        cityName: i.guildName.split('分桐')[0],
        description: i.summary,
        memberIds: i.previewMemberIds,
        status: i.status,
        recruitStatus: i.recruitStatus,
        subTitle
      }
    })
  }

  // 获取推荐分桐
  public async getRecommendCities() {
    const [, res] = await $service<{
      data: Array<IRecommendCitiesResp>
      page: number
      pageSize: number
      total: number
    }>({
      url: EnumApiTonghao.RecommendCitys,
      params: {
        page: 1,
        pageSize: 8
      }
    })

    if (res && Array.isArray(res.data) && res.data.length) {
      this.setCities(this.transformCityInfo(res.data))
    }
  }
  // 获取定位分桐
  public async getLocateRecommendCities(adcode: string) {
    const params: RecommendCityParamType = {
      adcode,
      recommend: true,
      page: 1,
      pageSize: 8,
      statusArr: [EnumCityStatus.Assistance, EnumCityStatus.Inited].join(',') // 1 助力中 2 已创建
    }

    const [, res] = await $service<{
      data: Array<IRecommendCitiesResp>
      page: number
      pageSize: number
      total: number
    }>({
      url: EnumApiTonghao.GuildList,
      params
    })

    if (res && Array.isArray(res.data) && res.data.length === 1) {
      this.setLocateCity(this.transformCityInfo(res.data)[0])
    }
  }
  // 加载城市级联信息
  public async loadCascadeCities() {
    const data = await getStaticJson<RawCityItem[]>(`/mocks/geojson/cities-300.json`)

    if (!data) {
      reportEntryLog('topic.load-cascade-cities.error', {
        msg: 'fetch error'
      })
      return null
    }

    return getCityCascader(data)
  }
  // 获取当前用户加入的分桐
  public async getJoinedCity() {
    const [, res] = await $service<IRecommendCitiesResp>({
      url: EnumApiTonghao.Myself
    })

    if (res && res.id) {
      this.setJoinedCity(this.transformCityInfo([res])[0])
      return true
    }

    return false
  }

  @action
  setJoinedCity(city: CityTypeInfo) {
    this.joinedCity = city
  }

  @action
  setLocateCity(city: CityTypeInfo) {
    this.locateCity = city
  }

  @action
  setCities(cities: Array<CityTypeInfo>) {
    this.cities = cities
  }

  @action
  reset() {
    this.cities = []
  }
}
